import { useMemo } from 'react';
import { useClubFeed, useHiddenUserIds, useMyLinks } from './queries';
import type { SocialPost } from './types';

/**
 * Thin wrapper around useClubFeed that flattens pages into a single posts array.
 * Filters out:
 *   - Posts from hidden/blocked users
 *   - Circle-only posts from users you're not linked with
 * Preserves the same return shape so callers don't need to change.
 */
export function useSocialFeed(clubId: string, channelSlug?: string | null) {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useClubFeed(clubId, channelSlug);

  const { data: hiddenIds = [] } = useHiddenUserIds(clubId);
  const { data: myLinks = [] } = useMyLinks(clubId, 'link');

  const posts: SocialPost[] = useMemo(() => {
    const allPosts = data?.pages.flatMap((p) => p?.posts || []) || [];

    const hiddenSet = new Set(hiddenIds);
    const linkedSet = new Set(myLinks.map((l) => l.target?.id).filter(Boolean));

    return allPosts.filter((post) => {
      // Filter hidden/blocked users
      if (hiddenSet.has(post.author_id)) return false;
      // Filter circle-only posts from users we're not linked with
      if ((post.metadata as any)?.circle_only && !linkedSet.has(post.author_id)) return false;
      return true;
    });
  }, [data, hiddenIds, myLinks]);

  return {
    posts,
    loading: isLoading,
    error: error?.message || null,
    loadMore: () => { if (hasNextPage) fetchNextPage(); },
    refresh: () => { refetch(); },
    hasMore: !!hasNextPage,
  };
}
